import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import { describeRpcError } from "@how/app-kit";
import { EmptyState, ErrorState, LoadingState, PageHeader, useSearchParamState } from "@how/ui";
import { CheckInTabs } from "./CheckInTabs";
import { Labels } from "./Labels";
import {
  OVERRIDE_LABEL,
  formatWhen,
  suggestRoom,
  useCheckIn,
  useCheckOut,
  useCloseSession,
  useDoorOccurrences,
  useFindHousehold,
  useOpenSession,
  useRooms,
  useSessionRoster,
  type FoundHousehold,
  type Label,
  type OverrideReason,
  type RosterRow,
} from "./check-in-queries";
import "./station.css";
import { semanticColor } from "@how/design-system";

function notifyError(title: string, error: unknown): void {
  const described = describeRpcError(error);
  notifications.show({
    color: described.kind === "denied" ? "yellow" : "red",
    title,
    message: described.message,
  });
}

/**
 * The station (ADR 0042, task 10): a screen for a tablet at a door. Open a session on a date, find a household,
 * tick who is coming in and the room, print the labels, and check out with the code — or an override with a
 * reason — and the collector's name. Everything a volunteer sees about a child is what the station's functions
 * return: a name, a household, a room, the codes (D12).
 */
export function StationPage() {
  const [sessionId, setSessionId] = useSearchParamState("session", "");
  return sessionId === "" ? (
    <OpenDoor
      onOpened={(id) => {
        setSessionId(id);
      }}
    />
  ) : (
    <Door
      sessionId={sessionId}
      onClosed={() => {
        setSessionId(undefined);
      }}
    />
  );
}

function OpenDoor({ onOpened }: { onOpened: (sessionId: string) => void }) {
  const occurrences = useDoorOccurrences();
  const open = useOpenSession();
  return (
    <>
      <PageHeader
        title="Check-in"
        description="Choose the date at this door. Opening it starts a session; every door on the same date shares it."
      />
      <CheckInTabs active="door" />
      {occurrences.isPending ? (
        <LoadingState rows={4} />
      ) : occurrences.isError ? (
        <ErrorState
          message="Could not load the dates."
          onRetry={() => {
            void occurrences.refetch();
          }}
        />
      ) : occurrences.data.length === 0 ? (
        <EmptyState
          title="Nothing on this week"
          description="A date has to exist under Events before a door can open on it."
        />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {occurrences.data.map((o) => (
            <Card key={o.occurrenceId}>
              <Stack gap="xs">
                <Text fw={600}>{o.eventTitle}</Text>
                <Text size="sm">{formatWhen(o.startsAt, o.campusTimeZone)}</Text>
                {o.campusName === null ? null : (
                  <Text size="xs" c="dimmed">
                    {o.campusName}
                  </Text>
                )}
                <Group justify="space-between" align="center" mt="xs">
                  {o.openSessionId === null ? (
                    <Text size="xs" c="dimmed">
                      Not open yet
                    </Text>
                  ) : (
                    <Badge variant="light" color="teal">
                      Open
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    loading={open.isPending && open.variables === o.occurrenceId}
                    onClick={() => {
                      open.mutate(o.occurrenceId, {
                        onSuccess: onOpened,
                        onError: (error) => {
                          notifyError("Could not open the door", error);
                        },
                      });
                    }}
                  >
                    {o.openSessionId === null ? "Open this door" : "Join this door"}
                  </Button>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </>
  );
}

function Door({ sessionId, onClosed }: { sessionId: string; onClosed: () => void }) {
  const roster = useSessionRoster(sessionId);
  const rooms = useRooms();
  const close = useCloseSession();
  const [search, setSearch] = useState("");
  const [labels, setLabels] = useState<Label[] | null>(null);
  const [closing, setClosing] = useState(false);
  const [checkingOut, setCheckingOut] = useState<RosterRow | null>(null);
  const session = roster.data?.session;
  const inRoom = (roster.data?.checkIns ?? []).filter((c) => c.checkedOutAt === null);
  const gone = (roster.data?.checkIns ?? []).filter((c) => c.checkedOutAt !== null);

  useEffect(() => {
    if (session?.closedAt) onClosed();
  }, [session?.closedAt, onClosed]);

  return (
    <>
      <PageHeader
        title={session === undefined ? "Check-in" : session.event}
        {...(session === undefined
          ? {}
          : {
              description: `${formatWhen(session.startsAt)} · ${String(inRoom.length)} in now, ${String(gone.length)} gone home`,
            })}
        actions={
          <Button
            variant="subtle"
            color="red"
            onClick={() => {
              setClosing(true);
            }}
          >
            Close this door
          </Button>
        }
      />
      {roster.isPending ? (
        <LoadingState rows={4} />
      ) : roster.isError ? (
        <ErrorState
          message={describeRpcError(roster.error).message}
          onRetry={() => {
            void roster.refetch();
          }}
        />
      ) : (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Stack gap="md">
            <Title order={3}>Arrivals</Title>
            <TextInput
              size="md"
              placeholder="Family or last name"
              aria-label="Find a household"
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
              }}
            />
            <Households
              sessionId={sessionId}
              search={search}
              rooms={rooms.data ?? []}
              onLabels={(printed) => {
                setLabels(printed);
                setSearch("");
              }}
            />
          </Stack>
          <Stack gap="md">
            <Title order={3}>In now</Title>
            {inRoom.length === 0 ? (
              <Text size="sm" c="dimmed">
                Nobody checked in yet.
              </Text>
            ) : (
              <Stack gap="xs">
                {inRoom.map((c) => (
                  <Card key={c.checkInId} padding="sm">
                    <Group justify="space-between" wrap="nowrap" align="flex-start">
                      <div>
                        <Group gap="xs" wrap="nowrap">
                          <Text fw={600}>
                            {c.firstName} {c.lastName}
                          </Text>
                          {c.isChild ? (
                            <Badge size="sm" variant="light" color={semanticColor("special")}>
                              Child
                            </Badge>
                          ) : null}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {[c.household, c.room, `Label ${c.labelCode}`]
                            .filter((p) => p !== null)
                            .join(" · ")}
                        </Text>
                      </div>
                      <Button
                        size="compact-sm"
                        variant="light"
                        onClick={() => {
                          setCheckingOut(c);
                        }}
                      >
                        Check out
                      </Button>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
            {gone.length > 0 ? (
              <Text size="xs" c="dimmed">
                Gone home:{" "}
                {gone
                  .map(
                    (c) =>
                      `${c.firstName} ${c.lastName}${c.collectedBy ? ` (with ${c.collectedBy}${c.collectorListed === false ? ", not on the list" : ""})` : ""}`,
                  )
                  .join(", ")}
              </Text>
            ) : null}
          </Stack>
        </SimpleGrid>
      )}

      <Modal
        opened={labels !== null}
        onClose={() => {
          setLabels(null);
        }}
        title="Labels"
        size="lg"
        radius="lg"
      >
        {labels === null ? null : (
          <Stack gap="sm">
            <Labels labels={labels} />
            <Group justify="flex-end" gap="sm">
              <Button
                variant="subtle"
                onClick={() => {
                  setLabels(null);
                }}
              >
                Done
              </Button>
              <Button
                onClick={() => {
                  window.print();
                }}
              >
                Print
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <CheckOutModal
        sessionId={sessionId}
        row={checkingOut}
        onClose={() => {
          setCheckingOut(null);
        }}
      />

      <Modal
        opened={closing}
        onClose={() => {
          setClosing(false);
        }}
        title="Close this door"
        radius="lg"
      >
        <Stack gap="sm">
          <Text size="sm">
            {inRoom.length === 0
              ? "Close the session? Every door on this date closes with it."
              : `${String(inRoom.length)} still checked in. Closing does not check them out; their rows stay as they are. Close anyway?`}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => {
                setClosing(false);
              }}
            >
              Keep open
            </Button>
            <Button
              color="red"
              loading={close.isPending}
              onClick={() => {
                close.mutate(sessionId, {
                  onSuccess: () => {
                    setClosing(false);
                    onClosed();
                  },
                  onError: (error) => {
                    notifyError("Could not close", error);
                  },
                });
              }}
            >
              Close the door
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function Households({
  sessionId,
  search,
  rooms,
  onLabels,
}: {
  sessionId: string;
  search: string;
  rooms: readonly {
    id: string;
    name: string;
    ageMin: number | null;
    ageMax: number | null;
    capacity: number | null;
  }[];
  onLabels: (labels: Label[]) => void;
}) {
  const found = useFindHousehold(sessionId, search);
  if (search.trim().length < 2) {
    return (
      <Text size="sm" c="dimmed">
        Type a family name to find who is arriving.
      </Text>
    );
  }
  if (found.isPending) return <LoadingState rows={2} />;
  if (found.isError) return <ErrorState message={describeRpcError(found.error).message} />;
  if (found.data.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Nobody matches. A new family is added under People first, then checked in.
      </Text>
    );
  }
  return (
    <Stack gap="sm">
      {found.data.map((h) => (
        <HouseholdCard
          key={h.householdId}
          sessionId={sessionId}
          household={h}
          rooms={rooms}
          onLabels={onLabels}
        />
      ))}
    </Stack>
  );
}

function HouseholdCard({
  sessionId,
  household,
  rooms,
  onLabels,
}: {
  sessionId: string;
  household: FoundHousehold;
  rooms: readonly {
    id: string;
    name: string;
    ageMin: number | null;
    ageMax: number | null;
    capacity: number | null;
  }[];
  onLabels: (labels: Label[]) => void;
}) {
  const checkIn = useCheckIn();
  const arriving = household.members.filter((m) => !m.checkedIn);
  const [chosen, setChosen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(arriving.map((m) => [m.personId, m.isChild])),
  );
  const [roomOf, setRoomOf] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(arriving.map((m) => [m.personId, suggestRoom(rooms, m)])),
  );
  const picked = useMemo(
    () => arriving.filter((m) => chosen[m.personId] === true),
    [arriving, chosen],
  );
  return (
    <Card>
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600}>{household.name}</Text>
          <Text size="xs" c="dimmed">
            {String(household.members.filter((m) => m.checkedIn).length)} already in
          </Text>
        </Group>
        {arriving.length === 0 ? (
          <Text size="sm" c="dimmed">
            Everyone in this household is checked in.
          </Text>
        ) : (
          arriving.map((m) => (
            <Group key={m.personId} justify="space-between" wrap="nowrap" align="center">
              <Checkbox
                size="md"
                label={`${m.firstName} ${m.lastName}${m.isChild ? "" : " (adult)"}`}
                checked={chosen[m.personId] === true}
                onChange={(event) => {
                  const on = event.currentTarget.checked;
                  setChosen((c) => ({ ...c, [m.personId]: on }));
                }}
              />
              {m.isChild ? (
                <Select
                  aria-label={`Room for ${m.firstName}`}
                  data={rooms.map((r) => ({ value: r.id, label: r.name }))}
                  value={roomOf[m.personId] ?? null}
                  placeholder="Room"
                  onChange={(value) => {
                    setRoomOf((r) => ({ ...r, [m.personId]: value }));
                  }}
                  w={160}
                />
              ) : null}
            </Group>
          ))
        )}
        {arriving.length > 0 ? (
          <Group justify="flex-end">
            <Button
              size="md"
              disabled={picked.length === 0}
              loading={checkIn.isPending}
              onClick={() => {
                checkIn.mutate(
                  {
                    sessionId,
                    people: picked.map((m) => ({
                      personId: m.personId,
                      locationId: roomOf[m.personId] ?? null,
                    })),
                  },
                  {
                    onSuccess: onLabels,
                    onError: (error) => {
                      notifyError("Could not check in", error);
                    },
                  },
                );
              }}
            >
              Check in {picked.length === 0 ? "" : `(${String(picked.length)})`}
            </Button>
          </Group>
        ) : null}
      </Stack>
    </Card>
  );
}

function CheckOutModal({
  sessionId,
  row,
  onClose,
}: {
  sessionId: string;
  row: RosterRow | null;
  onClose: () => void;
}) {
  const checkOut = useCheckOut();
  const [collectedBy, setCollectedBy] = useState("");
  const [code, setCode] = useState("");
  const [override, setOverride] = useState<OverrideReason | null>(null);
  const reset = () => {
    setCollectedBy("");
    setCode("");
    setOverride(null);
    onClose();
  };
  const child = row?.isChild === true;
  const collectors = row?.collectors ?? [];
  const ready =
    !child || (collectedBy.trim() !== "" && (code.trim().length === 3 || override !== null));
  return (
    <Modal
      opened={row !== null}
      onClose={reset}
      title={row === null ? "" : `Check out ${row.firstName} ${row.lastName}`}
      radius="lg"
    >
      <Stack gap="sm">
        {child ? (
          <>
            <Text size="sm" c="dimmed">
              A child goes home only with a named collector, and the code from the guardian&apos;s
              tag. If the tag is lost, use an override and say why; it is recorded as one.
            </Text>
            {collectors.length > 0 ? (
              <div>
                <Text size="sm" fw={600}>
                  Allowed to collect
                </Text>
                <Group gap={6} mt={4} wrap="wrap" aria-label="Allowed to collect">
                  {collectors.map((k) => (
                    <Button
                      key={`${k.source}-${k.name}`}
                      size="compact-sm"
                      variant={collectedBy === k.name ? "filled" : "light"}
                      onClick={() => {
                        setCollectedBy(k.name);
                      }}
                    >
                      {k.name}
                      {k.relationship === null || k.relationship === "household"
                        ? ""
                        : ` (${k.relationship})`}
                    </Button>
                  ))}
                </Group>
              </div>
            ) : null}
            <TextInput
              label="Who is collecting"
              size="md"
              required
              description="Someone not on the list may still collect; it is recorded as such."
              value={collectedBy}
              onChange={(event) => {
                setCollectedBy(event.currentTarget.value);
              }}
            />
            <TextInput
              label="Code on the tag"
              size="md"
              placeholder="ABC"
              maxLength={3}
              value={code}
              onChange={(event) => {
                setCode(event.currentTarget.value.toUpperCase());
              }}
              disabled={override !== null}
            />
            <Select
              label="No tag? Override with a reason"
              placeholder="No override"
              clearable
              data={(Object.keys(OVERRIDE_LABEL) as OverrideReason[]).map((k) => ({
                value: k,
                label: OVERRIDE_LABEL[k],
              }))}
              value={override}
              onChange={(value) => {
                setOverride(value === null ? null : (value as OverrideReason));
              }}
            />
          </>
        ) : (
          <Text size="sm" c="dimmed">
            An adult checks out with no code.
          </Text>
        )}
        {checkOut.isError ? (
          <Text size="sm" c="red">
            {describeRpcError(checkOut.error).message}
          </Text>
        ) : null}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={reset}>
            Cancel
          </Button>
          <Button
            size="md"
            disabled={!ready}
            loading={checkOut.isPending}
            onClick={() => {
              if (row === null) return;
              checkOut.mutate(
                {
                  sessionId,
                  checkInId: row.checkInId,
                  collectedBy,
                  securityCode: override === null ? code : "",
                  overrideReason: override,
                },
                { onSuccess: reset },
              );
            }}
          >
            Check out
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
